import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { CalendarMonth, PlaceRounded } from '@mui/icons-material';
import PlanYourJourneyModal from './PlanYourJourneyModal';

const DynamicPackageCard = ({ tourPackage }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [showAllPlaces, setShowAllPlaces] = useState(false);
  const [isJourneyModalOpen, setIsJourneyModalOpen] = useState(false);

  const packageId = tourPackage?.new_item_code || tourPackage?.id;
  const packageName = tourPackage?.package_name || tourPackage?.name || '';
  const days = tourPackage?.number_of_days || 0;
  const image =
    typeof tourPackage?.package_image === 'string'
      ? tourPackage.package_image
      : tourPackage?.package_image?.['Img 1'] || '';

  const places = Array.isArray(tourPackage?.places)
    ? [...tourPackage.places].sort((a, b) => Number(a.sequence_number) - Number(b.sequence_number))
    : [];

  const placesCount = places.length;
  const shouldTruncate = placesCount > 3;
  const visiblePlaces = showAllPlaces || !shouldTruncate ? places : places.slice(0, 2);
  const remainingCount = placesCount - 2;

  const handlePlanYourJourney = () => {
    setIsJourneyModalOpen(true);
  };

  const handleViewDetails = () => {
    navigate(`/destination/${slug}/${packageId}`, {
      state: { packageId: packageId }
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start'
      }}>
      <Box sx={{ width: '100%', height: 220, overflow: 'hidden', flexShrink: 0 }}>
        <img
          src={image}
          alt={packageName}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontFamily: 'gothamBold',
            fontSize: '18px',
            lineHeight: '24px',
            color: '#000',
            mb: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
          {packageName}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <CalendarMonth sx={{ fontSize: 18, color: '#000' }} />
            <Typography sx={{ fontFamily: 'gothamBold', fontSize: '14px', color: '#000' }}>
              {days}
            </Typography>
            <Typography sx={{ fontFamily: 'gothamBook', fontSize: '14px', color: '#000' }}>
              Days
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <PlaceRounded sx={{ fontSize: 18, color: '#000' }} />
            <Typography sx={{ fontFamily: 'gothamBold', fontSize: '14px', color: '#000' }}>
              {placesCount}
            </Typography>
            <Typography sx={{ fontFamily: 'gothamBook', fontSize: '14px', color: '#000' }}>
              Cities
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: '#f9f9fb',
            mx: -2.5,
            px: 2.5,
            py: 2,
            mb: 2
          }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            {visiblePlaces.map((place, i) => (
              <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Typography
                  component="span"
                  sx={{ fontFamily: 'gothamBook', fontSize: '13px', color: '#000' }}>
                  {place.Place}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'gothamBold',
                    fontSize: '13px',
                    color: '#000',
                    ml: 0.5
                  }}>
                  ({place['Number of Nights']}N)
                </Typography>
                {i < visiblePlaces.length - 1 && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'gothamBook',
                      fontSize: '13px',
                      color: '#000',
                      mx: 0.8
                    }}>
                    ---
                  </Typography>
                )}
              </Box>
            ))}

            {shouldTruncate && !showAllPlaces && (
              <Typography
                component="span"
                onClick={() => setShowAllPlaces(true)}
                sx={{
                  fontFamily: 'gothamBold',
                  fontSize: '13px',
                  color: '#27306B',
                  cursor: 'pointer',
                  ml: 0.5
                }}>
                --- +{remainingCount} Cities
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handlePlanYourJourney}
            sx={{
              flex: 1,
              color: '#000',
              border: '1px solid #ccc',
              borderRadius: '8px',
              fontFamily: 'gothamBook',
              fontSize: '14px',
              textTransform: 'none',
              py: 1.2,
              '&:hover': {
                border: '1px solid #ccc',
                backgroundColor: 'transparent'
              }
            }}>
            Get a free quote
          </Button>
          <Button
            onClick={handleViewDetails}
            sx={{
              flex: 1,
              backgroundColor: '#E41E25',
              color: '#fff',
              borderRadius: '8px',
              fontFamily: 'gothamBold',
              fontSize: '14px',
              textTransform: 'none',
              py: 1.2,
              '&:hover': {
                backgroundColor: '#c4191f'
              }
            }}>
            View Details
          </Button>
        </Box>
      </Box>

      <PlanYourJourneyModal
        open={isJourneyModalOpen}
        onClose={() => setIsJourneyModalOpen(false)}
        tourPackage={tourPackage}
      />
    </Box>
  );
};

export default DynamicPackageCard;
